import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Plus, Save, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import AdminLayout from '../components/admin/AdminLayout';
import DeleteRoleConfirmModal from '../components/admin/DeleteRoleConfirmModal';
import Button from '../components/Common/Button';
import {
  createAdminRole,
  deleteAdminRole,
  getAdminCompanies,
  getAdminPrivilegeCatalog,
  getAdminRoles,
  getMyPermissions,
  updateAdminRole,
} from '../constants';
import {
  getUserRole,
  hasPrivilege,
  isSuperAdmin,
  PRIVILEGE_LABELS,
  PRIVILEGES,
  setStoredPermissions,
} from '../utils/permissions';
import { validateRolePrivileges } from '../utils/rolePrivilegeValidation';
import '../assets/css/global.scss';

function PrivilegeChecklist({ selected, onToggle, disabled, catalog }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
      {catalog.map(({ key, label }) => (
        <label
          key={key}
          className={`flex items-start gap-2 text-sm ${disabled ? 'opacity-60' : 'cursor-pointer'}`}
        >
          <input
            type="checkbox"
            checked={selected.includes(key)}
            disabled={disabled}
            onChange={() => onToggle(key)}
            className="mt-0.5 accent-[#326AEB]"
          />
          <span className="text-[#0A2342] dark:text-white">
            {label || PRIVILEGE_LABELS[key] || key}
          </span>
        </label>
      ))}
    </div>
  );
}

PrivilegeChecklist.propTypes = {
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  catalog: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
    }),
  ).isRequired,
};

PrivilegeChecklist.defaultProps = {
  disabled: false,
};

const needsWorkspaceToEdit = (role, selectedCompanyId, isSa) =>
  !isSa && Boolean(role?.isSystem) && !selectedCompanyId;

const companyIdOf = (c) => String(c?._id || c?.id || '');

export default function AdminRoleManagement() {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [loading, setLoading] = useState(true);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDesc, setNewRoleDesc] = useState('');
  const [newPrivileges, setNewPrivileges] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editPrivileges, setEditPrivileges] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deletingRole, setDeletingRole] = useState(false);
  const [isSa, setIsSa] = useState(() => isSuperAdmin());
  const [canManageRoles, setCanManageRoles] = useState(
    () =>
      isSuperAdmin() ||
      hasPrivilege(PRIVILEGES.ROLE_MANAGEMENT) ||
      hasPrivilege(PRIVILEGES.ADMIN_BLOCK),
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const wasSuperAdmin = isSuperAdmin();
        const res = await getMyPermissions();
        if (cancelled) return;
        const payload = res?.data || res;
        const apiRole = String(payload?.role || '')
          .trim()
          .toLowerCase()
          .replace(/[_-]+/g, ' ')
          .replace(/\s+/g, ' ');
        const apiIsSa = apiRole === 'super admin';

        if (payload?.role && (apiIsSa || !wasSuperAdmin)) {
          try {
            const raw = sessionStorage.getItem('userData');
            const parsed = raw ? JSON.parse(raw) : {};
            sessionStorage.setItem(
              'userData',
              JSON.stringify({ ...parsed, role: payload.role }),
            );
          } catch {
            /* ignore */
          }
        }
        setStoredPermissions(payload);
        const sa = apiIsSa || wasSuperAdmin || isSuperAdmin();
        setIsSa(sa);
        const privs = payload?.privileges || [];
        setCanManageRoles(
          sa ||
            privs.includes(PRIVILEGES.ROLE_MANAGEMENT) ||
            privs.includes(PRIVILEGES.ADMIN_BLOCK),
        );
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!canManageRoles) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getAdminCompanies();
        if (cancelled) return;
        const list = res?.data || res || [];
        setCompanies(Array.isArray(list) ? list : []);
        if (!isSa && list.length && !selectedCompanyId) {
          setSelectedCompanyId(companyIdOf(list[0]));
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init company list once when access granted
  }, [canManageRoles, isSa]);

  const load = useCallback(async () => {
    if (!canManageRoles) {
      setLoading(false);
      return;
    }
    // Admins must pick a company; Super Admin can load all (no companyId)
    if (!isSa && !selectedCompanyId) {
      setLoading(false);
      setRoles([]);
      return;
    }
    setLoading(true);
    try {
      const companyId = isSa ? selectedCompanyId || undefined : selectedCompanyId;
      const [rolesRes, catalogRes] = await Promise.all([
        getAdminRoles(companyId),
        getAdminPrivilegeCatalog(companyId),
      ]);
      setRoles(rolesRes?.data || rolesRes || []);
      setCatalog(catalogRes?.data || catalogRes || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, [canManageRoles, isSa, selectedCompanyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!newRoleName.trim()) {
      toast.error('Role name is required');
      return;
    }
    if (!isSa && !selectedCompanyId) {
      toast.error('Select an organization first');
      return;
    }
    const privilegeCheck = validateRolePrivileges(newPrivileges);
    if (!privilegeCheck.valid) {
      toast.error(privilegeCheck.message);
      return;
    }
    try {
      await createAdminRole({
        name: newRoleName.trim(),
        description: newRoleDesc.trim(),
        privileges: newPrivileges,
        // Super Admin + workspace selected → role belongs to that org (visible to Org Admin).
        // Super Admin + All workspaces → global custom role.
        ...(selectedCompanyId ? { companyId: selectedCompanyId } : {}),
      });
      toast.success(
        selectedCompanyId
          ? 'Role created for this workspace'
          : 'Global role created — visible when a workspace is selected and in assignable roles',
      );
      setNewRoleName('');
      setNewRoleDesc('');
      setNewPrivileges([]);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to create role');
    }
  };

  const startEdit = (role) => {
    if (needsWorkspaceToEdit(role, selectedCompanyId, isSa)) {
      toast.error('Select a workspace first to customize this role for your organization.');
      return;
    }
    if (role.name === 'Super Admin') {
      toast.info('Super Admin always has full access across the product.');
      return;
    }
    setEditingId(role._id);
    setEditPrivileges(role.privileges || []);
  };

  const saveEdit = async (roleId) => {
    const role = roles.find((r) => String(r._id) === String(roleId));
    if (needsWorkspaceToEdit(role, selectedCompanyId, isSa)) {
      toast.error('Select a workspace first to customize this role for your organization.');
      return;
    }
    const privilegeCheck = validateRolePrivileges(editPrivileges);
    if (!privilegeCheck.valid) {
      toast.error(privilegeCheck.message);
      return;
    }
    try {
      await updateAdminRole(roleId, {
        privileges: editPrivileges,
        ...(selectedCompanyId ? { companyId: selectedCompanyId } : {}),
      });
      toast.success(
        selectedCompanyId
          ? 'Role updated for this workspace only — other workspaces and Super Admin are unchanged'
          : 'Role updated globally — applies as the default for workspaces without their own override',
      );
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update role');
    }
  };

  const openDeleteModal = (role) => {
    if (role.isSystem) {
      toast.error('Default system roles cannot be deleted');
      return;
    }
    setRoleToDelete(role);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deletingRole) return;
    setDeleteModalOpen(false);
    setRoleToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!roleToDelete) return;
    setDeletingRole(true);
    try {
      await deleteAdminRole(roleToDelete._id, {
        companyId: selectedCompanyId || roleToDelete.companyId || undefined,
      });
      toast.success('Role deleted');
      setDeleteModalOpen(false);
      setRoleToDelete(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to delete role');
    } finally {
      setDeletingRole(false);
    }
  };

  return (
    <AdminLayout title="Admin">
      <div className="px-[18px] pb-6">
        {!canManageRoles ? (
          <div className="bg-white dark:bg-[#182433] border border-[#D1E2F0] dark:border-[#25384F] rounded-lg p-6 max-w-2xl">
            <h2 className="text-lg font-semibold text-[#0A2342] dark:text-white mb-2">
              Roles &amp; Permissions
            </h2>
            <p className="text-sm text-[#6B7C93] dark:text-[#8FA3B8] mb-3">
              You need <b className="text-[#0A2342] dark:text-white">Admin Block</b> access for an
              organization to manage roles. Your current role is{' '}
              <b className="text-[#0A2342] dark:text-white">{getUserRole() || '—'}</b>.
            </p>
          </div>
        ) : (
          <>
            <div className="bg-white dark:bg-[#182433] border border-[#D1E2F0] dark:border-[#25384F] rounded-lg p-4 mb-4">
              <label className="block text-sm font-medium text-[#0A2342] dark:text-white mb-1">
                Workspace
              </label>
              <select
                value={selectedCompanyId}
                onChange={(e) => {
                  setSelectedCompanyId(e.target.value);
                  setEditingId(null);
                }}
                className="w-full max-w-md px-3 py-2 rounded-md border border-[#D1E2F0] dark:border-[#25384F] bg-white dark:bg-[#151F2C] text-sm dark:text-white"
              >
                {isSa ? <option value="">All workspaces</option> : null}
                {companies.map((c) => (
                  <option key={companyIdOf(c)} value={companyIdOf(c)}>
                    {c.companyName || c.name || companyIdOf(c)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-[#6B7C93] dark:text-[#8FA3B8] mt-2">
                {isSa
                  ? 'All workspaces (no workspace selected): edits apply as the default for all workspaces. Select a workspace (e.g. Trigent) to change privileges for that organization only — Super Admin access and other workspaces stay unchanged.'
                  : 'Select your workspace — role edits apply only to this organization. Super Admin and other workspaces are not affected.'}
              </p>
            </div>

            <div className="bg-white dark:bg-[#182433] border border-[#D1E2F0] dark:border-[#25384F] rounded-lg p-4 mb-6">
              <h2 className="text-lg font-semibold text-[#0A2342] dark:text-white mb-3">
                Create new role
              </h2>
              <p className="text-xs text-[#6B7C93] dark:text-[#8FA3B8] mt-2 mb-3 max-w-xl">
                {isSa
                  ? 'Select a workspace before creating so the role belongs to that organization (Org Admins can see and assign it). With All workspaces selected, the role is created globally.'
                  : 'New roles are created for the selected workspace only.'}
              </p>
              <div className="grid gap-3 max-w-xl">
                <input
                  type="text"
                  placeholder="Role name (e.g. Release Viewer)"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  className="px-3 py-2 rounded-md border border-[#D1E2F0] dark:border-[#25384F] bg-white dark:bg-[#151F2C] text-sm dark:text-white"
                />
                <input
                  type="text"
                  placeholder="Description (optional)"
                  value={newRoleDesc}
                  onChange={(e) => setNewRoleDesc(e.target.value)}
                  className="px-3 py-2 rounded-md border border-[#D1E2F0] dark:border-[#25384F] bg-white dark:bg-[#151F2C] text-sm dark:text-white"
                />
              </div>
              <p className="text-sm font-medium text-[#0A2342] dark:text-white mt-4 mb-1">
                Privileges
              </p>
              <PrivilegeChecklist
                catalog={catalog}
                selected={newPrivileges}
                onToggle={(key) =>
                  setNewPrivileges((prev) =>
                    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
                  )
                }
              />
              <Button variant="primary" className="mt-4" onClick={handleCreate}>
                <Plus className="w-4 h-4 inline mr-1" />
                Create role
              </Button>
            </div>

            <div className="bg-white dark:bg-[#182433] border border-[#D1E2F0] dark:border-[#25384F] rounded-lg p-4">
              <h2 className="text-lg font-semibold text-[#0A2342] dark:text-white mb-4">
                Existing roles
              </h2>
              {loading && <p className="text-sm text-[#6B7C93]">Loading...</p>}
              {!loading && !isSa && !selectedCompanyId && (
                <p className="text-sm text-[#6B7C93]">Select an organization to view roles.</p>
              )}
              {!loading && roles.length === 0 && (isSa || selectedCompanyId) && (
                <p className="text-sm text-[#6B7C93]">No roles found.</p>
              )}
              <div className="space-y-4">
                {roles.map((role) => (
                  <div
                    key={`${role._id}-${role.companyId || 'global'}`}
                    className="border border-[#D1E2F0] dark:border-[#25384F] rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <h3 className="font-semibold text-[#0A2342] dark:text-white">
                          {role.name}
                          {role.isSystem && (
                            <span className="ml-2 text-xs font-normal text-[#6B7C93]">
                              (system)
                            </span>
                          )}
                          {(role.companyOverride || role.isCompanyOverride) && (
                            <span className="ml-2 text-xs font-normal text-[#326AEB]">
                              (this organization)
                            </span>
                          )}
                          {role.inheritedGlobal && (
                            <span className="ml-2 text-xs font-normal text-[#6B7C93]">
                              (global — visible to all workspaces)
                            </span>
                          )}
                          {!role.isSystem &&
                            role.companyId &&
                            !role.inheritedGlobal &&
                            !role.companyOverride &&
                            !role.isCompanyOverride && (
                              <span className="ml-2 text-xs font-normal text-[#326AEB]">
                                (this organization)
                              </span>
                            )}
                        </h3>
                        {role.description && (
                          <p className="text-xs text-[#6B7C93] mt-0.5">{role.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {role.name !== 'Super Admin' &&
                          (editingId === role._id ? (
                            <Button variant="primary" onClick={() => saveEdit(role._id)}>
                              <Save className="w-4 h-4 inline mr-1" />
                              Save
                            </Button>
                          ) : (
                            <Button
                              variant="secondary"
                              onClick={() => startEdit(role)}
                              disabled={
                                needsWorkspaceToEdit(role, selectedCompanyId, isSa) ||
                                (!isSa && Boolean(role.inheritedGlobal || (!role.companyId && !role.isSystem)))
                              }
                              title={
                                needsWorkspaceToEdit(role, selectedCompanyId, isSa)
                                  ? 'Select a workspace to customize this role for your organization'
                                  : !isSa && (role.inheritedGlobal || (!role.companyId && !role.isSystem))
                                    ? 'Only Super Admin can edit global custom roles'
                                    : isSa && role.isSystem
                                      ? 'Edits apply to all workspaces'
                                      : undefined
                              }
                            >
                              Edit privileges
                            </Button>
                          ))}
                        {!role.isSystem &&
                          (isSa || (role.companyId && !role.inheritedGlobal)) && (
                          <button
                            type="button"
                            onClick={() => openDeleteModal(role)}
                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                            aria-label="Delete role"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    {editingId === role._id ? (
                      <PrivilegeChecklist
                        catalog={catalog}
                        selected={editPrivileges}
                        onToggle={(key) =>
                          setEditPrivileges((prev) =>
                            prev.includes(key)
                              ? prev.filter((k) => k !== key)
                              : [...prev, key],
                          )
                        }
                        disabled={role.name === 'Super Admin'}
                      />
                    ) : (
                      <p className="text-xs text-[#6B7C93] dark:text-[#8FA3B8] mt-2">
                        {(role.privileges || []).length} privilege(s) assigned
                        {role.name === 'Employee' && !(role.privileges || []).length
                          ? ' · default: Standup only'
                          : ''}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
      <DeleteRoleConfirmModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        role={roleToDelete}
        onConfirm={handleConfirmDelete}
        deleting={deletingRole}
      />
    </AdminLayout>
  );
}
